import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./useAuth";
import { useGraphQLMutation } from "./useGraphQLMutation";
import { RECORD_TRIAL_PAGE_VIEW } from "@/graphql/trials";

/**
 * Hook that automatically tracks page views for trial users.
 * Should be used in a top-level component like AppLayout.
 *
 * Tracks:
 * - Page path (URL pathname)
 * - Page title (document.title)
 *
 * Only tracks for active trial users and debounces rapid navigation.
 */
export function useTrialPageTracking() {
  const location = useLocation();
  const { isTrialUser, isTrialExpired } = useAuth();
  const lastTrackedPath = useRef<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const recordPageViewMutation = useGraphQLMutation<
    { recordTrialPageView: boolean },
    { pagePath: string; pageTitle?: string }
  >(RECORD_TRIAL_PAGE_VIEW);

  useEffect(() => {
    // Only track for active trial users
    if (!isTrialUser || isTrialExpired) {
      return;
    }

    const currentPath = location.pathname;

    // Don't track the same path twice in a row
    if (currentPath === lastTrackedPath.current) {
      return;
    }

    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce to avoid tracking rapid navigation
    debounceTimer.current = setTimeout(() => {
      lastTrackedPath.current = currentPath;

      // Get page title (may not be immediately available on SPA navigation)
      const pageTitle = document.title || undefined;

      recordPageViewMutation.mutate({
        pagePath: currentPath,
        pageTitle,
      });
    }, 500); // 500ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [location.pathname, isTrialUser, isTrialExpired]);
}
